/**
 * Ingredients Table Component
 *
 * Editable table of recipe ingredients.
 */

import { useState } from 'react';
import { Trash2, AlertCircle, AlertTriangle } from 'lucide-react';
import { formatCentimes, getQuantityStep } from '@/lib/format';
import type { IngredientState } from '../../hooks/useRecipeBuilder';

interface IngredientsTableProps {
  ingredients: IngredientState[];
  currency?: string;
  onUpdateIngredient: (id: string, field: 'quantity' | 'notes', value: unknown) => void;
  onRemoveIngredient: (id: string) => void;
  errors?: Record<string, string>;
  showFinancials?: boolean;
}

export function IngredientsTable({
  ingredients,
  currency = 'EUR',
  onUpdateIngredient,
  onRemoveIngredient,
  errors = {},
  showFinancials = false,
}: IngredientsTableProps) {
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Filter out deleted ingredients for display
  const visibleIngredients = ingredients.filter((i) => !i.isDeleted);

  // Format cost using currency
  const formatCost = (cost: number | null) => {
    if (cost === null) return '-';
    return formatCentimes(cost, currency);
  };

  const handleQuantityChange = (id: string, value: string) => {
    const num = parseFloat(value);
    if (!isNaN(num) && num >= 0) {
      onUpdateIngredient(id, 'quantity', num);
    } else if (value === '') {
      onUpdateIngredient(id, 'quantity', 0);
    }
  };

  const handleDelete = (id: string) => {
    const ingredient = ingredients.find((i) => i.id === id);
    if (ingredient && ingredient.quantity > 0 && !confirmDeleteId) {
      setConfirmDeleteId(id);
    } else {
      onRemoveIngredient(id);
      setConfirmDeleteId(null);
    }
  };

  if (visibleIngredients.length === 0) {
    return (
      <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center">
        <div className="text-neutral-400 mb-2">
          <AlertCircle className="w-8 h-8 mx-auto" />
        </div>
        <p className="text-sm text-neutral-500">
          Aucun ingredient ajoute. Utilisez la recherche ci-dessus pour ajouter des produits bruts.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-neutral-50 border-b border-neutral-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                Produit brut
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide w-28">
                Quantite
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide w-20">
                Unite
              </th>
              {showFinancials && (
                <th className="text-right px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide w-32">
                  Cout
                </th>
              )}
              <th className="text-right px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide w-20">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {visibleIngredients.map((ingredient, index) => {
              const hasError = !!errors[`ingredient_${ingredient.id}`];
              const isConfirmingDelete = confirmDeleteId === ingredient.id;
              const isInactive = ingredient.rawProduct.isActive === false;

              return (
                <tr
                  key={ingredient.id}
                  className={`${index % 2 === 1 ? 'bg-neutral-50/50' : ''} ${
                    hasError ? 'bg-danger-50/50' : ''
                  } ${isInactive ? 'bg-warning-50/50' : ''}`}
                >
                  {/* Product Name */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-dark-500">{ingredient.rawProduct.name}</span>
                      {isInactive && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-warning-100 text-warning-700">
                          <AlertTriangle className="w-3 h-3" />
                          Archive
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-neutral-400">{ingredient.rawProduct.sku}</div>
                    {isInactive && (
                      <p className="text-xs text-warning-600 mt-0.5">
                        Produit inactif — remplacez-le ou reactivez-le pour sauvegarder
                      </p>
                    )}
                  </td>

                  {/* Quantity */}
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      value={ingredient.quantity || ''}
                      onChange={(e) => handleQuantityChange(ingredient.id, e.target.value)}
                      step={getQuantityStep()}
                      min="0"
                      className={`w-full px-3 py-1.5 border rounded-lg text-sm text-dark-500
                                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                                  transition-all ${
                                    hasError ? 'border-danger-500 bg-danger-50' : 'border-neutral-200 bg-white'
                                  }`}
                      placeholder="0"
                    />
                    {hasError && (
                      <p className="text-xs text-danger-500 mt-1">
                        {errors[`ingredient_${ingredient.id}`]}
                      </p>
                    )}
                  </td>

                  {/* Unit */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-neutral-600">
                      {ingredient.rawProduct.recipeUnit.symbol}
                    </span>
                  </td>

                  {/* Cost - only for managers+ */}
                  {showFinancials && (
                    <td className="px-4 py-3 text-right">
                      {ingredient.cost !== null ? (
                        <span className="text-sm font-medium text-dark-500">
                          {formatCost(ingredient.cost)}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-warning-100 text-warning-700">
                          PMP manquant
                        </span>
                      )}
                    </td>
                  )}

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end">
                      {isConfirmingDelete ? (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteId(null)}
                            className="px-2 py-1 text-xs text-neutral-500 hover:text-neutral-700 transition-colors"
                          >
                            Annuler
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(ingredient.id)}
                            className="px-2 py-1 text-xs text-danger-500 hover:text-danger-700 font-medium transition-colors"
                          >
                            Confirmer
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleDelete(ingredient.id)}
                          className="p-1.5 text-neutral-400 hover:text-danger-500 hover:bg-danger-50
                                     rounded-lg transition-all"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
